import type { PagingQuery } from '@/lib/api/common'

/**
 * Inventory, as `/v1/inventory` speaks it.
 *
 * A stock item is what the business counts — a product it sells, or the milk
 * and cups that go into one. It is not a catalogue product: a product may link
 * to one (`productId`), an ingredient never does, and the two are edited in
 * different places.
 *
 * Quantities and money arrive as JSON numbers, never strings.
 */

export const StockItemStatus = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
  /** Soft-deleted. Hidden from the list unless asked for by name. */
  Archived: 'ARCHIVED',
} as const

export type StockItemStatus = (typeof StockItemStatus)[keyof typeof StockItemStatus]

/** What kind of thing an item is. The API calls this filter `category`; see `StockItemQuery`. */
export const StockItemType = {
  Product: 'PRODUCT',
  Ingredient: 'INGREDIENT',
  Packaging: 'PACKAGING',
  Material: 'MATERIAL',
} as const

export type StockItemType = (typeof StockItemType)[keyof typeof StockItemType]

/**
 * A unit's code, as `GET /units` lists it — `kg`, `ml`, `each`, or a business's
 * own `jin`. Data, not a fixed set: which codes exist, and which convert into
 * which, comes from the units list (see `useUnitCatalog`).
 */
export type UnitOfMeasure = string

/** What a unit measures, as `/units` groups them. `own` is a merchant's own unit — a jin, a slice. */
export type InventoryUnitFamily = 'weight' | 'volume' | 'count' | 'own'

/**
 * A unit, as `GET /units` lists them: the system's own, then the business's.
 *
 * Two units stand in for each other when they share a base — `baseCode`, or the
 * unit's own code when it is a base: kg and g (base g), lb and oz (base oz). One
 * of them is `factorToBase` of its base, or 1 for the base itself.
 */
export interface InventoryUnit {
  id: string
  family: InventoryUnitFamily
  code: string
  /** How many of `baseCode` are in one of this (kg → 1000 g). `null` for a base unit or an own one. */
  factorToBase: number | null
  baseCode: string | null
  /** The UN/ECE Rec 20 code an e-invoice uses (KGM, LTR, H87). */
  uomCode: string | null
  status: 'active' | 'inactive'
  /** Shipped with the service — nobody edits it. `false` for a business's own unit. */
  isSystem: boolean
  createdAt: string
  updatedAt: string
}

export interface InventoryUnitQuery extends PagingQuery {
  family?: InventoryUnitFamily
  status?: InventoryUnit['status']
}

/** How an item comes to be. */
export const ConstructionType = {
  /** Bought or received as is. */
  Simple: 'SIMPLE',
  /** Made from other items, by a recipe. */
  Composite: 'COMPOSITE',
} as const

export type ConstructionType = (typeof ConstructionType)[keyof typeof ConstructionType]

/** `NON_STOCKED` is listed but never counted — its quantities stay at zero. */
export type TrackingType = 'STOCKED' | 'NON_STOCKED'

export type CostMethod = 'AVCO' | 'FIFO'

export const InventoryCategoryStatus = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
} as const

export type InventoryCategoryStatus =
  (typeof InventoryCategoryStatus)[keyof typeof InventoryCategoryStatus]

/** Inventory's own grouping — Dairy, Packaging — not the catalogue's categories. */
export interface InventoryCategory {
  id: string
  name: string
  description: string | null
  status: InventoryCategoryStatus
  createdAt: string
  updatedAt: string
}

export interface StockItem {
  id: string
  /** The catalogue product this item counts, when it counts one. */
  productId: string | null
  variantId: string | null
  /** `null` when the item has not been put in a category. */
  category: { id: string; name: string } | null
  sku: string
  name: string
  stockItemType: StockItemType
  constructionType: ConstructionType
  trackingType: TrackingType
  unitOfMeasure: UnitOfMeasure
  /** At or below this, the item wants reordering. */
  reorderPoint: number
  safetyBuffer: number
  /**
   * The three quantities are totals across EVERY location. A count per location
   * is a separate read — `/stock`.
   */
  onHand: number
  reserved: number
  /**
   * `onHand − reserved`. Can be negative: a stock count that finds less than the
   * ledger says writes the shortfall down rather than rounding it away.
   */
  available: number
  costMethod: CostMethod
  currentAvgCost: number
  /** The cost of one unit until a receipt sets one. `0` when never given. */
  defaultUnitCost: number
  status: StockItemStatus
  /** Sent back on every update — the API refuses a stale one. */
  version: number
  createdAt: string
  updatedAt: string
}

export interface StockItemQuery extends PagingQuery {
  /** Omit for active and inactive together; archived items only show when asked for. */
  status?: StockItemStatus
  /**
   * The item TYPE, despite the name — the API's word for `stockItemType`. The
   * Category entity is `categoryId`.
   */
  category?: StockItemType
  categoryId?: string
  /** Name or SKU, any part of it, ignoring case. */
  search?: string
}

/**
 * A new stock item, as `POST /items` takes it.
 *
 * The optional keys are left off rather than sent empty: the service rejects an
 * empty `productId` outright, and an absent one is simply "not linked".
 */
export interface CreateStockItemBody {
  sku: string
  name: string
  /** The catalogue product this item counts. */
  productId?: string
  /** The catalogue variant this item counts. One item per variant, per business. */
  variantId?: string
  categoryId?: string
  stockItemType: StockItemType
  constructionType: ConstructionType
  trackingType: TrackingType
  unitOfMeasure: UnitOfMeasure
  /** Up to 3 decimals, never negative. */
  reorderPoint?: number
  safetyBuffer?: number
  costMethod?: CostMethod
  /** The cost of one unit until a receipt sets one. Up to 6 decimals, never negative. */
  defaultUnitCost?: number
}

/**
 * A change to a stock item, as `PATCH /items/:id` takes it — only the fields
 * that changed. `version` is the one last read; the API refuses a stale one with
 * a conflict.
 *
 * `unitOfMeasure`, `constructionType` and `trackingType` are frozen once the item
 * has any stock movement: the API refuses a change to them after that.
 */
export interface UpdateStockItemBody {
  version: number
  sku?: string
  name?: string
  /** `null` takes the item out of its category. */
  categoryId?: string | null
  unitOfMeasure?: UnitOfMeasure
  constructionType?: ConstructionType
  costMethod?: CostMethod
  /** Up to 3 decimals, never negative. */
  reorderPoint?: number
  safetyBuffer?: number
  /** Up to 6 decimals, never negative. */
  defaultUnitCost?: number
  /** Archiving is its own call; an update only moves between these two. */
  status?: 'ACTIVE' | 'INACTIVE'
}

/**
 * What the list's filters hold. `null` is "no filter" and `''` is "no search" —
 * both are left off the query, never sent empty. The type is not among them:
 * products and ingredients are separate lists, chosen by `StockItemKind`.
 */
export interface StockItemFilterValues {
  search: string
  status: StockItemStatus | null
  categoryId: string | null
}

/** The two lists the Stock Items tab splits into, each one type of item. */
export type StockItemKind = typeof StockItemType.Product | typeof StockItemType.Ingredient

export interface InventoryCategoryQuery extends PagingQuery {
  status?: InventoryCategoryStatus
  /** Any part of the name, ignoring case. */
  search?: string
}

/** A branch, or a warehouse. Mirrored from the main API: read-only here. */
export interface InventoryLocation {
  id: string
  type: 'branch' | 'warehouse'
  /**
   * The outlet: the branch itself, or the branch that owns this warehouse.
   * `null` for a central warehouse that belongs to none.
   */
  branchId: string | null
  name: string
  status: 'active' | 'inactive'
  createdAt: string
  updatedAt: string
}

export interface InventoryLocationQuery extends PagingQuery {
  type?: InventoryLocation['type']
  status?: InventoryLocation['status']
  /** The outlet itself, plus the warehouses it owns. */
  branchId?: string
}

/**
 * The batch read: these items, at this one location.
 *
 * Not paged — the service ignores `limit` and `offset` here and answers one row
 * per item asked about, zeros for an item that has never moved at that location.
 */
export interface StockLevelQuery {
  locationId: string
  /** At most 50. */
  itemIds: string[]
}

/** One item across every location: its totals, and the levels they add up from. */
export interface ItemStock {
  itemId: string
  onHand: number
  reserved: number
  available: number
  /** Only the locations the item has a level at — never a row of zeros for the rest. */
  locations: StockLevel[]
}

/**
 * Where a recipe is in its life.
 *
 * `DRAFT` is the empty shell every COMPOSITE item is born with: named, no
 * ingredients, and not used for anything until it is filled in. Filling it in
 * and saving makes it `ACTIVE`. `INACTIVE` is archived.
 */
export const RecipeStatus = {
  Draft: 'DRAFT',
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
} as const

export type RecipeStatus = (typeof RecipeStatus)[keyof typeof RecipeStatus]

export interface RecipeLine {
  id: string
  /** The ingredient — another stock item. */
  itemId: string
  qty: number
  unitOfMeasure: UnitOfMeasure
  sortOrder: number
}

/** What goes into a COMPOSITE item — its bill of materials. One per item at most. */
export interface Recipe {
  id: string
  stockItemId: string
  name: string
  /** How much one run of the recipe makes, in `yieldUnit`. */
  yieldQty: number
  yieldUnit: UnitOfMeasure
  status: RecipeStatus
  /** Sent back on every update — the API refuses a stale one. */
  version: number
  /** In the order the recipe lists them. */
  lines: RecipeLine[]
}

export interface RecipeQuery extends PagingQuery {
  /** The recipe of this item — 0 or 1 rows. */
  stockItemId?: string
  /** The recipes that use this item as an ingredient. */
  ingredientId?: string
}

/** One ingredient line on the way up. Up to 4 decimals, more than 0. */
export interface RecipeLineInput {
  stockItemId: string
  qty: number
  /** Must measure the same thing as the ingredient's own unit — mass, volume or count. */
  unitOfMeasure: UnitOfMeasure
}

export interface CreateRecipeBody {
  stockItemId: string
  name: string
  yieldQty?: number
  yieldUnit: UnitOfMeasure
  lines: RecipeLineInput[]
}

/**
 * A change to a recipe. `version` is the one last read; `lines`, when sent,
 * replace the whole list. `ACTIVE` needs at least one line; `DRAFT` cannot be set.
 */
export interface UpdateRecipeBody {
  version: number
  name?: string
  yieldQty?: number
  yieldUnit?: UnitOfMeasure
  lines?: RecipeLineInput[]
  status?: typeof RecipeStatus.Active | typeof RecipeStatus.Inactive
}

/** One item at one location. */
export interface StockLevel {
  itemId: string
  locationId: string
  onHand: number
  reserved: number
  available: number
  /** One unit of the item's own unit: its average cost, or its default before there is one. */
  unitCost: number
  /** `onHand × unitCost`. */
  stockValue: number
  /** `null` for an item that has never moved here — reported as zeros. */
  updatedAt: string | null
}

/** Every item's level at every location, most recently changed first. */
export interface StockLevelListQuery extends PagingQuery {
  locationId?: string
  itemId?: string
}

/**
 * What moved stock. Signed by `qty`: in is positive, out negative. A `CONSUMPTION`
 * is a sale's deduction, or (positive) its reversal by a void, waste or comp.
 * `WASTE_RETURN` is on old rows only.
 */
export type MovementType =
  | 'RECEIPT'
  | 'CONSUMPTION'
  | 'RETURN'
  | 'WASTE'
  | 'WASTE_RETURN'
  | 'ADJUSTMENT'
  | 'TRANSFER_OUT'
  | 'TRANSFER_IN'

/**
 * The document a movement came from — `sourceId` is its id. `ORDER`,
 * `POS_TRANSACTION` and `WASTAGE` are on old rows only.
 */
export type MovementSourceType =
  | 'ORDER_FIRE'
  | 'ORDER_VOID'
  | 'ORDER_WASTE'
  | 'ORDER_COMP'
  | 'SHIPMENT'
  | 'REFUND'
  | 'PURCHASE_ORDER'
  | 'STOCK_COUNT'
  | 'COUNT_ANCHOR'
  | 'TRANSFER'
  | 'ADJUSTMENT'
  | 'PRODUCTION_RUN'
  | 'SYSTEM'
  | 'ORDER'
  | 'POS_TRANSACTION'
  | 'WASTAGE'

/** One line of the stock ledger. Never edited: a correction is another movement. */
export interface StockMovement {
  id: string
  itemId: string
  locationId: string
  type: MovementType
  /** Signed: positive in, negative out. In `unitOfMeasure`, the item's own unit. */
  qty: number
  unitOfMeasure: UnitOfMeasure
  /** The cost of one unit the row is stamped with. `null` on transfers. */
  unitCost: number | null
  /** Why — set on every `WASTE` and `ADJUSTMENT` (`/reason-codes`). */
  reasonCodeId: string | null
  sourceType: MovementSourceType
  sourceId: string
  sourceLineId: string | null
  /** A sale's own quantity, for a sale's `CONSUMPTION`. */
  soldQty: number | null
  /** How far a sale took on hand below zero; 0 otherwise. */
  shortfallQty: number
  reservationId: string | null
  referenceMovementId: string | null
  /** A document's note, or the POS's own reason text on a waste or comp. */
  notes: string | null
  createdBy: string
  occurredAt: string
}

export interface StockMovementQuery extends PagingQuery {
  type?: MovementType
  itemId?: string
  locationId?: string
  /** ISO-8601, inclusive. */
  from?: string
  to?: string
  sourceType?: MovementSourceType
  sourceId?: string
}

/** `INACTIVE` is archived: kept on the recipe, not applied to a sale. */
export type RecipeModifierStatus = 'ACTIVE' | 'INACTIVE'

/** What picking a modifier does to the recipe: add an ingredient, take one away, or swap. */
export type RecipeModifierMode = 'ADD' | 'REMOVE' | 'REPLACE'

/**
 * One ingredient a modifier adds or removes.
 *
 * `quantity` is the amount in effect: stored for `FIXED`, read from the base
 * recipe's line for `BASE_COMPONENT` (which is why it is never typed in for that).
 */
export interface RecipeModifierComponent {
  stockItemId: string
  quantity: number
  unitOfMeasure: UnitOfMeasure
  effectType: 'ADD' | 'REMOVE'
  quantityMode: 'BASE_COMPONENT' | 'FIXED'
}

/** A choice on a recipe — extra shot, oat milk — and what it does to the stock used. */
export interface RecipeModifier {
  id: string
  recipeId: string
  name: string
  /** The catalogue's modifier it is linked to, when it came from there. */
  catalogModifierRef: string | null
  status: RecipeModifierStatus
  /** `false` for a modifier that only changes the price or the label, never the stock. */
  affectsStock: boolean
  /** `null` when it does not affect stock. */
  mode: RecipeModifierMode | null
  components: RecipeModifierComponent[]
}

/**
 * One component on the way up.
 *
 * `BASE_COMPONENT` takes its quantity from the recipe — the base line for a
 * REMOVE, the removed amount for the one ADD of a swap — so it carries no
 * quantity or unit. `FIXED` carries both.
 */
export type RecipeModifierComponentInput =
  | {
      stockItemId: string
      effectType: 'ADD' | 'REMOVE'
      quantityMode: 'BASE_COMPONENT'
    }
  | {
      stockItemId: string
      effectType: 'ADD' | 'REMOVE'
      quantityMode: 'FIXED'
      /** Up to 4 decimals, more than 0. */
      quantity: number
      /** Must measure the same thing as the ingredient — mass, volume or count. */
      unitOfMeasure: UnitOfMeasure
    }

/**
 * A new modifier on a recipe. A modifier that does not affect stock takes no
 * mode and no components; one that does needs both.
 */
export interface CreateRecipeModifierBody {
  name: string
  /** The menu choice it mirrors; the menu's renames and removals then reach it. */
  catalogModifierRef?: string
  affectsStock: boolean
  mode?: RecipeModifierMode
  components?: RecipeModifierComponentInput[]
}

/**
 * A change to a modifier; what is left out stays. `catalogModifierRef: null`
 * unlinks it from the menu. Turning stock on, or changing the mode, needs the
 * components sent with it — the service will not keep ones made for another
 * mode. There is no `status`: only the menu's own events archive a modifier.
 */
export interface UpdateRecipeModifierBody {
  name?: string
  catalogModifierRef?: string | null
  affectsStock?: boolean
  mode?: RecipeModifierMode
  components?: RecipeModifierComponentInput[]
}

/** Only paging: the service has no filter on a recipe's modifiers. */
export type RecipeModifierQuery = PagingQuery

export interface InventoryLocationFilterValues {
  type: InventoryLocation['type'] | null
  status: InventoryLocation['status'] | null
}

/**
 * The fixed bucket a reason sits in. It decides the movement: `waste`,
 * `internal_use` and `comp` write `WASTE`, the rest `ADJUSTMENT`.
 */
export type ReasonCategory =
  'waste' | 'internal_use' | 'comp' | 'shrinkage' | 'correction' | 'opening'

/** Why stock was wasted or adjusted: one NexDots ships, or the business's own. */
export interface ReasonCode {
  id: string
  category: ReasonCategory
  name: string
  /** Shipped by NexDots: the same for every business, never changed. */
  shipped: boolean
  /** Written only by the service (count variance, opening balance…); never offered in a picker. */
  system: boolean
  status: 'active' | 'deactivated'
}

export interface ReasonCodeQuery extends PagingQuery {
  category?: ReasonCategory
  status?: ReasonCode['status']
  /** For reading the ledger; a picker leaves system reasons out. */
  includeSystem?: boolean
}

/**
 * One document type, three kinds: `adjustment` adds or removes a known quantity,
 * `wastage` writes off stock that left without a sale, `opening` puts the
 * starting quantity and cost.
 */
export type AdjustmentKind = 'adjustment' | 'wastage' | 'opening'

/**
 * A `draft` has moved nothing. Submitting posts it, or leaves it
 * `pending_approval` when its value is over the business's threshold.
 */
export type AdjustmentStatus = 'draft' | 'pending_approval' | 'posted' | 'cancelled'

/** One line, in the item's own unit (a menu item: how many). */
export interface AdjustmentLine {
  id: string
  /** A stock item — or, on a wastage, `variantId`: a whole menu item. */
  itemId: string | null
  variantId: string | null
  optionRefs: string[] | null
  /** Signed on an `adjustment`; positive on `wastage` and `opening`. */
  qty: number
  packId: string | null
  packQty: number | null
  reasonCodeId: string
  /** Typed only on an opening line, or found stock of an item with no cost yet. */
  unitCost: number | null
}

export interface Adjustment {
  id: string
  kind: AdjustmentKind
  locationId: string
  status: AdjustmentStatus
  /** When it physically happened; may be earlier than when it was entered. */
  occurredAt: string
  note: string | null
  clientDocId: string
  /** The opening-balance CSV it came from. */
  importId: string | null
  /** Σ|qty × cost| at submit — what the approval threshold was checked against. */
  approvalValue: number | null
  createdBy: string
  submittedBy: string | null
  submittedAt: string | null
  approvedBy: string | null
  approvedAt: string | null
  /** Approved by the person who submitted it — allowed, and shown. */
  selfApproved: boolean | null
  postedAt: string | null
  cancelledAt: string | null
  createdAt: string
  lines: AdjustmentLine[]
}

/**
 * One line on the way up. `qty`, or `packId` with `packQty`. A reason on every
 * line except an opening's; `unitCost` only where the service asks for it.
 */
export interface AdjustmentLineInput {
  itemId?: string
  variantId?: string
  optionRefs?: string[]
  qty?: number
  packId?: string
  packQty?: number
  reasonCodeId?: string
  unitCost?: number
}

export interface CreateAdjustmentBody {
  kind: AdjustmentKind
  locationId: string
  /** ISO-8601; defaults to now, never in the future. */
  occurredAt?: string
  note?: string
  /** The form's own id for the document: sending it again returns the same one. */
  clientDocId: string
  lines: AdjustmentLineInput[]
}

export interface AdjustmentQuery extends PagingQuery {
  kind?: AdjustmentKind
  status?: AdjustmentStatus
  locationId?: string
}

/** `full` lists every counted item; `partial` the ones picked. */
export type CountScope = 'full' | 'partial'

export type CountStatus = 'draft' | 'counting' | 'pending_approval' | 'posted' | 'cancelled'

/** Not counted is not zero: a line never counted is left alone when the count posts. */
export type CountLineState = 'not_counted' | 'counted'

/** Why a counted line asks "are you sure?" — never a refusal. */
export type CountLineWarning = 'EXPECTED_ZERO' | 'LARGE_VARIANCE'

export interface CountLineEntry {
  packId: string | null
  unitOfMeasure: string | null
  enteredQty: number
  /** In the item's own unit. */
  qty: number
}

export interface CountLine {
  id: string
  itemId: string
  state: CountLineState
  /** In the item's own unit; `null` while not counted. */
  countedQty: number | null
  countedAt: string | null
  /** On hand as at `countedAt`. `null` on a blind count until it posts. */
  expectedQty: number | null
  varianceQty: number | null
  unitCost: number | null
  movementId: string | null
  entries: CountLineEntry[]
  /** Only on the answer to entering the line. */
  warning?: CountLineWarning | null
}

/** A physical stocktake at one location. */
export interface StockCount {
  id: string
  locationId: string
  scope: CountScope
  blind: boolean
  status: CountStatus
  isOpening: boolean
  note: string | null
  clientDocId: string
  approvalValue: number | null
  createdBy: string
  submittedBy: string | null
  submittedAt: string | null
  approvedBy: string | null
  approvedAt: string | null
  selfApproved: boolean | null
  postedAt: string | null
  cancelledAt: string | null
  createdAt: string
  lines: CountLine[]
  /** On create: items also on another open count at the location. */
  warnings?: Array<{ itemId: string; countId: string }>
}

export interface CreateCountBody {
  locationId: string
  scope: CountScope
  /** `partial` only. */
  itemIds?: string[]
  /** Defaults to the business's setting. */
  blind?: boolean
  clientDocId: string
  note?: string
}

/** One entry of a line: in a pack of the item, or a unit of its family (its own by default). */
export interface CountEntryInput {
  qty: number
  packId?: string
  unitOfMeasure?: string
}

export interface EnterCountLineBody {
  /** Replaces the line's entries; empty puts it back to not counted. */
  entries: CountEntryInput[]
  countedAt?: string
}

export interface CountQuery extends PagingQuery {
  status?: CountStatus
  locationId?: string
}
